/**
 * Pure helpers for the electrical grounding & bonding schedule (NEC Article
 * 250). The engine emits an optional `grounding_schedule` on the raw electrical
 * Output — present only when the opt-in grounding stage ran, `null`/absent
 * otherwise (grounding-off outputs serialize byte-identically). These helpers
 * reshape that schedule into the flat rows the results table renders, tolerating
 * an absent schedule (returns empty rows). Deterministic, no IO — unit-testable
 * as plain functions (mirrors ../mep/raceways).
 */
import type {
  GroundingSchedule,
  GroundingScheduleConductorRow,
  GroundingScheduleElectrodeRow,
} from "../api/types";

/** One flattened conductor row for the schedule table. */
export interface GroundingConductorTableRow {
  kind: string;
  label: string;
  size_awg: string;
  from_node: string;
  to_node: string;
  citation: string;
}

/** One flattened electrode row for the schedule table. */
export interface GroundingElectrodeTableRow {
  kind: string;
  label: string;
  gec_size_awg: string;
  citation: string;
}

/** The schedule split into the two rendered sections. */
export interface GroundingScheduleView {
  conductors: GroundingConductorTableRow[];
  electrodes: GroundingElectrodeTableRow[];
  /** True when there is nothing to render (no stage, or empty rows). */
  isEmpty: boolean;
}

/**
 * Normalize the raw `grounding_schedule` (which is `null`/absent when the
 * grounding stage did not run) into the flat view the table renders. An absent
 * or empty schedule yields empty sections with `isEmpty: true` so the view can
 * show a "no grounding" placeholder instead of an empty table.
 */
export function groundingScheduleView(
  schedule: GroundingSchedule | null | undefined,
): GroundingScheduleView {
  const conductorRows: GroundingScheduleConductorRow[] = Array.isArray(schedule?.conductor_rows)
    ? schedule.conductor_rows
    : [];
  const electrodeRows: GroundingScheduleElectrodeRow[] = Array.isArray(schedule?.electrode_rows)
    ? schedule.electrode_rows
    : [];
  const conductors = conductorRows.map((r) => ({
    kind: r.kind,
    label: r.label,
    size_awg: r.size_awg,
    from_node: r.from_node,
    to_node: r.to_node,
    citation: r.citation,
  }));
  const electrodes = electrodeRows.map((r) => ({
    kind: r.kind,
    label: r.label,
    gec_size_awg: r.gec_size_awg,
    citation: r.citation,
  }));
  return {
    conductors,
    electrodes,
    isEmpty: conductors.length === 0 && electrodes.length === 0,
  };
}
